import type { HubNotification } from "./types.js";

// ── The three tabs a notification can be in, and what each one offers ───────
// ONE vocabulary, in shared, because the bell renders the tabs, the store
// filters by them and the route validates against them. Three separate lists of
// the same three words is how a tab comes to offer an action its own server
// refuses — the shape the feedback tabs were rebuilt around (see
// lib/feedbackBuckets.ts, the same lesson on a different surface).
//
// A bucket is DERIVED from the two timestamps, never stored. Archiving leaves
// `readAt` alone, so "restoring returns it to the tab it came from, with its
// read state intact" is a property of the derivation rather than a field
// remembering where the row used to be.

export type NotificationBucket = "unread" | "read" | "archived";

export const NOTIFICATION_BUCKETS: readonly { key: NotificationBucket; label: string }[] = [
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
  { key: "archived", label: "Archived" },
];

/** Every action a notification can be put through. */
export type NotificationAction = "read" | "unread" | "archive" | "restore" | "delete";

/**
 * The actions a bucket offers, in the order they are shown.
 *
 * Answered per BUCKET rather than per row, so the single-row buttons and the
 * bulk bar cannot offer different things — they read this list. `restore` only
 * appears in Archived, and `archive` never does: archiving an archived row is a
 * no-op dressed up as a control.
 */
export function actionsFor(bucket: NotificationBucket): readonly NotificationAction[] {
  switch (bucket) {
    case "unread": return ["read", "archive", "delete"];
    case "read": return ["unread", "archive", "delete"];
    case "archived": return ["restore", "delete"];
  }
}

export const ACTION_LABELS: Record<NotificationAction, string> = {
  read: "Mark read",
  unread: "Mark unread",
  archive: "Archive",
  restore: "Restore",
  delete: "Delete",
};

/** Which bucket a row is in. Archived wins: an archived row is out of both the
 *  Unread and the Read tab whatever its read state, which is what "archiving an
 *  unread notification removes it from the bell's unread count" means. */
export function bucketOf(n: Pick<HubNotification, "readAt" | "archivedAt">): NotificationBucket {
  if (n.archivedAt) return "archived";
  return n.readAt ? "read" : "unread";
}

/** Parse a bucket off the wire. Returns null for anything else, so a route can
 *  answer 400 rather than silently listing the wrong tab. */
export function parseBucket(raw: unknown): NotificationBucket | null {
  return NOTIFICATION_BUCKETS.some((b) => b.key === raw) ? (raw as NotificationBucket) : null;
}

export function parseAction(raw: unknown): NotificationAction | null {
  return raw === "read" || raw === "unread" || raw === "archive" || raw === "restore" || raw === "delete"
    ? raw
    : null;
}

/** Deleting is the one action that cannot be undone, so it is the one a bulk
 *  selection confirms. A single delete does not — see the ticket's own split. */
export function needsConfirm(action: NotificationAction, count: number): boolean {
  return action === "delete" && count > 1;
}
